import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  MonthlyReceipt,
  MonthlyTimes,
  SchambeckUser,
  downloadFile,
  getLohnlisteCSVForMonth,
  getOrCreateMonthlyReceiptsForMonth,
  getOverviewForMonth,
  getTimeSheetForMonth,
  getTimeTrackingsForOfficeAndYear,
  lockMultipleMonthlyReceipts,
  updateMultipleMonthlyReceipts,
  useData,
} from 'shared';
import { Language } from 'shared/src/Language/Language.types';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { GenerateMonthlyOverviewBoxes } from '../../components/timeTrackingOverviewBoxes/TimeTrackingOverviewBoxes';
import { useUser } from '../../components/UserContext';
import { convertMonthlyReceiptIntoTableEntry } from '../../utils/monthlyReceipt/MonthlyReceipt.utils';

/**
 * Summary page to display the monthlyOverviews of workingTimes for different months and years
 * @returns page to display the overview
 */
const MonthlySummary: React.FC = () => {
  const { user, axios, selectedOffices } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateNow: Date = new Date();
  const [selectedMonthAndYear, setSelectedMonthAndYear] = useState<[number, number]>([
    dateNow.getMonth(),
    dateNow.getFullYear(),
  ]);
  const [monthlyOverviews, setMonthlyOverviews] = useState<MonthlyReceipt[]>([]);
  const { data: users } = useData('SCHAMBECK_USERS_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: offices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const [loadedWorkTimes, setLoadedWorkTimes] = useState<MonthlyTimes>();
  const [documentToShow, setDocumentToShow] = useState<Blob>();
  const [isLoading, toggleLoading] = useState<boolean>(false);

  /**
   * Helper method to download a timesheet preview for a single user
   * @param user the user to create the document for
   */
  const handleDownloadTimeSheet = useCallback(
    async (user: SchambeckUser): Promise<void> => {
      getTimeSheetForMonth(axios, user.id, [
        selectedMonthAndYear[0] + 1,
        selectedMonthAndYear[1],
      ]).then(setDocumentToShow);
    },
    [axios, selectedMonthAndYear],
  );

  /**
   * Hook to load the monthly timeOverviews if year is changed
   */
  useEffect(() => {
    if (!selectedMonthAndYear[1] || selectedOffices.length === 0) return;
    toggleLoading(true);
    getTimeTrackingsForOfficeAndYear(axios, selectedMonthAndYear[1]).then((loadedWorkTimes) => {
      setLoadedWorkTimes(loadedWorkTimes);
      toggleLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonthAndYear[1], selectedOffices]);

  /**
   * Helper to get and set the receipts for the selected month
   * @param selected
   */
  const getReceiptsForMonth = (selected: [number, number]): void => {
    toggleLoading(true);
    getOrCreateMonthlyReceiptsForMonth(axios, selected[0], selected[1], user.id).then(
      (loadedOverviews) => {
        setMonthlyOverviews(loadedOverviews);
        toggleLoading(false);
      },
    );
  };
  //TableRows containing the monthly reciepts
  const rows: TableRow[] = useMemo((): TableRow[] => {
    if (selectedOffices.length > 1) return [];
    const hrEmployees: string[] | undefined = offices.find(
      (office) => office.id === selectedOffices[0],
    )?.hrEmployees;
    return convertMonthlyReceiptIntoTableEntry(
      monthlyOverviews,
      users,
      selectedMonthAndYear[1],
      setMonthlyOverviews,
      handleDownloadTimeSheet,
      !!hrEmployees?.includes(user.id),
      navigate,
    );
  }, [
    handleDownloadTimeSheet,
    monthlyOverviews,
    offices,
    selectedMonthAndYear,
    selectedOffices,
    user.id,
    users,
    navigate,
  ]);

  /**
   * Helper method to handle the download of monthlyOverviews
   */
  const handlePdfDownload: () => Promise<void> = async () => {
    const overview: Blob | undefined = await getOverviewForMonth(
      axios,
      selectedMonthAndYear[0] + 1,
      selectedMonthAndYear[1],
    );
    if (overview)
      downloadFile(
        overview,
        t('pages.monthlySummary.fileNamePDF', {
          replace: {
            month: selectedMonthAndYear[0] + 1,
            year: selectedMonthAndYear[1],
          },
        }),
      );
  };

  /**
   * Helper method to handle the download of the lonliste csv file
   */
  const handleCSVDownload: () => Promise<void> = async () => {
    const csvFile: Blob | undefined = await getLohnlisteCSVForMonth(
      axios,
      selectedMonthAndYear[0] + 1,
      selectedMonthAndYear[1],
      user.language || Language.DE,
    );
    if (csvFile) {
      downloadFile(
        csvFile,
        t('pages.monthlySummary.fileNameCSV', {
          replace: {
            month: selectedMonthAndYear[0] + 1,
            year: selectedMonthAndYear[1],
          },
        }),
      );
    }
  };

  return (
    <>
      <TopBar title={t('pages.monthlySummary.children.topBarHeadline')}>
        <div className="topbar__content">
          <Button
            value={<IconComponent type={IconType.SAVE} className="save-icon" />}
            disabled={monthlyOverviews.length === 0}
            onClick={() => updateMultipleMonthlyReceipts(axios, monthlyOverviews)}
          />
          <IconComponent
            type={IconType.DOWNLOAD_FILE}
            className={selectedOffices.length != 1 ? 'icon-disabled' : 'clickable'}
            onClick={() => {
              if (monthlyOverviews.length > 0 && selectedOffices.length === 1) handlePdfDownload();
            }}
          />
        </div>
      </TopBar>
      {documentToShow && (
        <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
          <object
            data={URL.createObjectURL(documentToShow)}
            type={documentToShow.type}
            width="1000px"
            height="700px"
          />
        </Popup>
      )}
      <Box>
        <GenerateMonthlyOverviewBoxes
          isLoading={isLoading}
          selectedMonthAndYear={selectedMonthAndYear}
          setSelectedMonthAndYear={(selected) => {
            setSelectedMonthAndYear(selected);
            getReceiptsForMonth(selected);
          }}
          monthlyTimes={loadedWorkTimes}
        />
      </Box>
      {!isLoading && (
        <Box>
          <form id="receipt-edit-form">
            <div className="monthly-summary__table-head">
              <p className="monthly-summary__table-headline">
                {t(`general.monthAndYear.${selectedMonthAndYear[0]}`, {
                  replace: { year: selectedMonthAndYear[1] },
                })}
              </p>
              <div className="monthly-summary__table-head__buttons">
                <Button
                  value={t('pages.monthlySummary.children.lock')}
                  backgroundColor="#BC2E46"
                  disabled={
                    monthlyOverviews.length === 0 || selectedOffices.length > 1 || isLoading
                  }
                  onClick={() => {
                    updateMultipleMonthlyReceipts(axios, monthlyOverviews).then((savedReceipts) =>
                      lockMultipleMonthlyReceipts(
                        axios,
                        savedReceipts.map((receipt) => receipt.id),
                      ).then((file) => {
                        if (file) {
                          downloadFile(
                            file,
                            t('pages.monthlySummary.fileNameZIP', {
                              replace: {
                                month: selectedMonthAndYear[0] + 1,
                                year: selectedMonthAndYear[1],
                              },
                            }),
                          );
                          window.location.reload();
                        }
                      }),
                    );
                  }}
                />
                <IconComponent
                  type={IconType.DOWNLOAD_FILE}
                  width={30}
                  onClick={() => {
                    if (monthlyOverviews.length > 0) handleCSVDownload();
                  }}
                />
              </div>
            </div>
            <Table
              header={
                t('pages.monthlySummary.tableHeader', {
                  returnObjects: true,
                }) as TableHeader[]
              }
              expandable
              rows={rows}
            />
          </form>
        </Box>
      )}
    </>
  );
};

export default MonthlySummary;
